import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionContext';
import Loading from './Loading';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
  /** Required permissions - user must have at least one of these */
  requiredPermissions: string[];
  /** If true, user must have ALL permissions. Default: false (any permission) */
  requireAll?: boolean;
  /** Where to redirect if access denied. Default: '/' */
  redirectTo?: string;
  /** Custom message for access denied */
  accessDeniedMessage?: string;
}

/**
 * PermissionProtectedRoute - Protects routes based on user permissions from groups
 * 
 * Usage:
 * <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.EMPLOYEES_VIEW]}>
 *   <EmployeeList />
 * </PermissionProtectedRoute>
 * 
 * // Require any of multiple permissions
 * <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.EMPLOYEES_VIEW, PERMISSIONS.EMPLOYEES_EDIT]}>
 *   <EmployeeList />
 * </PermissionProtectedRoute>
 * 
 * // Require ALL permissions
 * <PermissionProtectedRoute 
 *   requiredPermissions={[PERMISSIONS.EMPLOYEES_VIEW, PERMISSIONS.EMPLOYEES_EDIT]} 
 *   requireAll={true}
 * >
 *   <EmployeeList />
 * </PermissionProtectedRoute>
 */
const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({
  children,
  requiredPermissions,
  requireAll = false,
  redirectTo = '/',
  accessDeniedMessage,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  // Show loading while fetching permissions
  if (isLoading) {
    return <Loading text="Checking permissions..." />;
  }

  // Check permissions
  let hasAccess = false;
  
  if (requiredPermissions.length === 0) {
    // No permissions required - allow access
    hasAccess = true;
  } else if (requiredPermissions.length === 1) {
    // Single permission check
    hasAccess = hasPermission(requiredPermissions[0]);
  } else if (requireAll) {
    // Must have ALL permissions
    hasAccess = hasAllPermissions(requiredPermissions);
  } else {
    // Must have ANY permission
    hasAccess = hasAnyPermission(requiredPermissions);
  }

  if (!hasAccess) {
    // Log access denied for debugging
    console.warn(
      `Access denied. Required permissions: ${requiredPermissions.join(', ')}. ` +
      `Require all: ${requireAll}`
    );
    
    // Could show an access denied page instead of redirect
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default PermissionProtectedRoute;
